/**
 * 菜单选中状态匹配工具
 *
 * 背景：菜单状态区分「菜单模式（MENU_MODE）」和「卡片模式（CARD_MODE）」，两者匹配规则不同：
 * - 菜单模式：列表页与它的详情页都要选中同一个菜单项，按「路径段前缀」匹配；
 * - 卡片模式：卡片代表一个模块，模块详情页也要保持卡片高亮，按「包含」匹配。
 *
 * 注意：菜单模式不能用 endsWith。详情页地址是「列表路径 + /Detail/:id」，
 * 精确匹配必然失败，而匹配失败会走到"回退到第一个菜单"的逻辑，表现为菜单跳回概况。
 *
 * 之前每次从 getNavMode()/localStorage 现取，切换模式后同一渲染帧内可能拿到旧值，
 * 导致详情页刷新菜单选中状态；这里收敛成一处，由调用方显式传入当前模式。
 */

export const CARD_MODE = "card";
export const MENU_MODE = "menu";

export type NavMode = typeof CARD_MODE | typeof MENU_MODE;

type MenuNode = {
  id?: string;
  url?: string;
  children?: MenuNode[];
  [key: string]: any;
};

type MatchContext = {
  /** react-router 的 pathname，history 模式子应用用 */
  pathname?: string;
  /** location.hash，hash 模式子应用（khlinks）用 */
  hash?: string;
  /** 当前导航模式 */
  menuType?: string;
  /** APP_NAME 前缀，用于拼接 history 模式下的完整地址 */
  appName?: string;
};

/** 业务参数，不参与路由匹配 */
const IGNORED_SEARCH_KEYS = ["tenant", "refresh"];

const isIgnoredKey = (key: string) =>
  IGNORED_SEARCH_KEYS.reduce(
    (acc, ignored) => acc || key.replace(/\[\]$/, "") === ignored,
    false
  );

/**
 * 去掉业务 query、尾部斜杠、末尾 "/index"，得到可比较的路径
 * 注意不能直接按第一个 "?" 截断：hash 路由里 query 在 # 之后，
 * 而 tenant/refresh 会拼在菜单 url 末尾，粗暴截断会把 "#" 一起截掉。
 * 另外卡片/菜单模式下页面可能存在尾斜杠（/kh_overview/device-count/），需要归一化。
 */
export const normalizePath = (value: string = "") => {
  if (!value) {
    return "";
  }
  let path = value;
  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) {
    const search = path.slice(queryIndex + 1);
    const kept = search
      .split("&")
      .filter((pair) => pair && !isIgnoredKey(pair.split("=")[0] || ""));
    path = kept.length
      ? `${path.slice(0, queryIndex)}?${kept.join("&")}`
      : path.slice(0, queryIndex);
  }
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  return path;
};

/**
 * 去掉业务 query（tenant / refresh 等），用于从地址反推菜单
 */
export const stripIgnoredSearch = (url: string = "") => {
  if (!url) {
    return url;
  }
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

/**
 * target 是否以「完整路径段」的形式出现在 path 中
 *
 * 详情页地址是「列表路径 + /Detail/:id」，菜单模式必须能匹配到列表菜单，
 * 否则匹配失败会触发"回退到默认菜单"的跳转，也就是菜单跳回概况的问题。
 * 同时用边界判断避免 /Instance 误命中 /Instance2。
 */
const matchesAsPathSegment = (path: string, target: string) => {
  if (!path || !target) {
    return false;
  }
  let index = path.indexOf(target);
  while (index !== -1) {
    const next = path[index + target.length];
    if (next === undefined || next === "/" || next === "?" || next === "#") {
      return true;
    }
    index = path.indexOf(target, index + 1);
  }
  return false;
};

/**
 * 当前地址是否命中某个菜单
 */
export const matchMenuUrl = (menuUrl: string, ctx: MatchContext = {}) => {
  if (!menuUrl) {
    return false;
  }
  const { pathname = "", hash = "", menuType, appName = "" } = ctx;
  const mode = menuType || CARD_MODE;

  const wholeUrl =
    (appName && !appName.startsWith("/") ? "/" : "") + appName + menuUrl;
  const targetPath = normalizePath(wholeUrl);
  const rawPath = normalizePath(menuUrl);

  if (pathname && targetPath && pathname.includes(targetPath)) {
    return true;
  }

  if (!hash) {
    return false;
  }
  const hashPath = normalizePath(hash);
  // 卡片模式：模块详情页保持高亮
  if (mode === CARD_MODE) {
    return Boolean(rawPath) && hashPath.includes(rawPath);
  }
  // 菜单模式：详情页 / 详情子页同样命中所属列表菜单，按路径段前缀判断；
  // 多个菜单同时命中时由 findMenuChain 取最深的那个，不会误选父级。
  return Boolean(rawPath) && matchesAsPathSegment(hashPath, rawPath);
};

/**
 * 在菜单树里查找匹配当前地址的节点链（由根到当前菜单）
 * @returns 命中节点数组；未命中返回空数组
 */
export const findMenuChain = (
  menus: MenuNode[] = [],
  ctx: MatchContext = {}
): MenuNode[] => {
  let bestChain: MenuNode[] = [];

  const walk = (nodes: MenuNode[] = [], parentChain: MenuNode[] = []) => {
    nodes.forEach((node) => {
      if (!node) {
        return;
      }
      const chain = [...parentChain, node];
      if (matchMenuUrl(node.url || "", ctx)) {
        const currentLength = node.url?.length || 0;
        const bestLast = bestChain[bestChain.length - 1];
        const bestLength = bestLast?.url?.length || 0;
        // 同等层级时取路径更长（更具体）的菜单
        if (
          chain.length > bestChain.length ||
          (chain.length === bestChain.length && currentLength > bestLength)
        ) {
          bestChain = chain;
        }
      }
      if (node.children?.length) {
        walk(node.children, chain);
      }
    });
  };

  walk(menus);
  return bestChain;
};
